import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast as sonnerToast } from 'sonner';
import { clinicLeadAPI } from '../../utils/api';

// SALES-CRM-SIMPLIFICATION — presentation only. `human_stage` / `sales_context` come from the
// backend projection (`status_transitions`); this file never derives them from lead.status.
const STAGE_STYLE = {
  NEW: 'bg-sky-100 text-sky-800',
  WORKING: 'bg-indigo-100 text-indigo-800',
  ON_HOLD: 'bg-slate-200 text-slate-700',
  LOST: 'bg-rose-100 text-rose-700',
  WON: 'bg-emerald-100 text-emerald-800',
};

export const SalesStageBadge = ({ transitions, fallbackLabel, className = '' }) => {
  const { t } = useTranslation();
  const stage = transitions?.human_stage;
  if (!stage) return <Badge className={`bg-gray-100 text-gray-700 ${className}`} data-testid="sales-stage-badge-unknown">{fallbackLabel || '—'}</Badge>;
  const ctx = transitions?.sales_context;
  return (
    <div className={`inline-flex flex-col items-start gap-0.5 ${className}`} data-testid="sales-stage">
      <Badge className={`${STAGE_STYLE[stage] || 'bg-gray-100 text-gray-700'} text-xs`} data-testid={`sales-stage-badge-${stage}`}>
        {t(`provider.leads.stage.${stage}`)}
      </Badge>
      {ctx && (
        <span className="text-[11px] text-slate-500 leading-tight" data-testid={`sales-context-${ctx}`}>
          {t(`provider.leads.context.${ctx}`)}
        </span>
      )}
    </div>
  );
};

const ReasonPrompt = ({ label, onConfirm, onCancel, testId }) => {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  return (
    <div className="flex items-center gap-1 flex-wrap" data-testid={`${testId}-reason-prompt`}>
      <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t('provider.leads.actions.reasonPlaceholder')} className="h-8 text-xs w-44" data-testid={`${testId}-reason-input`} />
      <Button size="sm" className="h-8" onClick={() => onConfirm(reason)} data-testid={`${testId}-confirm`}>{label}</Button>
      <Button size="sm" variant="ghost" className="h-8" onClick={onCancel} data-testid={`${testId}-cancel`}>{t('provider.leads.actions.cancel')}</Button>
    </div>
  );
};

export const SalesActions = ({ lead, transitions, onStatus, onMarkWon, onDone, compact = false, idSuffix = '' }) => {
  const sfx = idSuffix ? `-${idSuffix}` : '';
  const { t } = useTranslation();
  const [pending, setPending] = useState(null); // 'LOST' | 'ON_HOLD'
  const [busy, setBusy] = useState(false);
  if (!transitions?.human_stage) return null;
  const stage = transitions.human_stage;
  const allowed = transitions.allowed_next || [];
  const size = compact ? 'h-7 px-2 text-xs' : 'h-8 text-xs';

  const run = async (fn) => {
    setBusy(true);
    try { await fn(); } finally { setBusy(false); setPending(null); }
  };
  const reopen = () => run(async () => {
    try {
      const { data } = await clinicLeadAPI.reopen(lead.id);
      sonnerToast.success(t(stage === 'ON_HOLD' ? 'provider.leads.actions.resumed' : 'provider.leads.reopen.success'));
      onDone && onDone(data);
    } catch (e) {
      sonnerToast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || t('common.error'));
    }
  });

  if (pending) {
    return (
      <ReasonPrompt
        testId={`sales-action-${pending}${sfx}`}
        label={t(pending === 'LOST' ? 'provider.leads.actions.markLost' : 'provider.leads.actions.putOnHold')}
        onConfirm={(reason) => run(() => onStatus(pending, reason))}
        onCancel={() => setPending(null)}
      />
    );
  }

  return (
    <div className="flex items-center gap-1 flex-wrap" data-testid={`sales-actions-${stage}${sfx}`}>
      {stage === 'NEW' && allowed.includes('CONTACTED') && (
        <Button size="sm" className={size} disabled={busy} onClick={() => run(() => onStatus('CONTACTED'))} data-testid={`sales-action-start-working${sfx}`}>
          {t('provider.leads.actions.startWorking')}
        </Button>
      )}
      {(stage === 'NEW' || stage === 'WORKING') && allowed.includes('ON_HOLD') && (
        <Button size="sm" variant="outline" className={size} disabled={busy} onClick={() => setPending('ON_HOLD')} data-testid={`sales-action-put-on-hold${sfx}`}>
          {t('provider.leads.actions.putOnHold')}
        </Button>
      )}
      {stage !== 'LOST' && stage !== 'WON' && allowed.includes('LOST') && (
        <Button size="sm" variant="outline" className={`${size} border-rose-200 text-rose-700`} disabled={busy} onClick={() => setPending('LOST')} data-testid={`sales-action-mark-lost${sfx}`}>
          {t('provider.leads.actions.markLost')}
        </Button>
      )}
      {stage === 'ON_HOLD' && transitions.can_resume && (
        <Button size="sm" className={size} disabled={busy} onClick={reopen} data-testid={`sales-action-resume${sfx}`}>
          {t('provider.leads.actions.resume')}
        </Button>
      )}
      {stage === 'LOST' && transitions.can_reopen && (
        <Button size="sm" variant="outline" className={size} disabled={busy} onClick={reopen} data-testid={`reopen-lead-btn${sfx}`}>
          {t('provider.leads.reopen.button')}
        </Button>
      )}
      {transitions.can_mark_won && onMarkWon && (
        <Button size="sm" variant="outline" className={`${size} border-emerald-300 text-emerald-700`} disabled={busy} onClick={onMarkWon} data-testid={`mark-won-btn${sfx}`}>
          {t('provider.leads.markWon.button')}
        </Button>
      )}
    </div>
  );
};
